/**
 * One curated master, and everything that follows from editing it.
 *
 * The reason this is a page rather than a form wedged above the list: an edit here is not
 * a local change. A vendor listing READS this row rather than copying it, so correcting a
 * name reaches every shop selling the product and every shopper looking at it — and the
 * old inline editor asked for that edit with the reach printed in one grey sentence, if it
 * had loaded yet.
 *
 * So the reach leads: it is a tile, it is in the header, and it is repeated on the retire
 * button, which is the one action here that a vendor would notice.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Boxes,
  Clock,
  Images,
  Info,
  Percent,
  PillBottle,
  Save,
  Store,
  Undo2,
  Users,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { DetailPanel, Field, FieldList, PanelSkeleton } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/error-state';
import { Label } from '@/components/ui/label';
import { notify } from '@/components/ui/toaster';
import { CatalogGalleryEditor } from '@/features/catalog-products/components/catalog-gallery-editor';
import { CommissionEditor } from '@/features/commission/components/commission-editor';
import { ago, date_time } from '@/lib/format';
import {
  use_catalog_product,
  use_create_catalog_product,
  use_set_catalog_commission,
  use_update_catalog_product,
} from '@/features/catalog-products/hooks';
import {
  FEE_SOURCE_LABEL,
  FEE_SOURCE_TONE,
  VERTICAL_LABEL,
  fee_amount,
  photo_count,
  reach,
  type CatalogProduct,
  type Vertical,
} from '@/features/catalog-products/types';
import { use_product_categories } from '@/features/product-categories/hooks';
import { category_label, type ProductCategory } from '@/features/product-categories/types';

type Draft = {
  name: string;
  brand: string;
  category: string;
  pack_size: string;
  description: string;
  is_prescription_required: boolean;
};

const empty_draft = (): Draft => ({
  name: '',
  brand: '',
  // Filled in from the admin's own list once it lands — there is no category this file
  // can assume exists any more.
  category: '',
  pack_size: '',
  description: '',
  is_prescription_required: false,
});

const from_product = (p: CatalogProduct): Draft => ({
  name: p.name,
  brand: p.brand ?? '',
  category: p.category,
  pack_size: p.pack_size ?? '',
  description: p.description ?? '',
  is_prescription_required: p.is_prescription_required,
});

/** Blank strings mean "no value" to the API, not an empty string. */
const or_null = (s: string) => (s.trim() === '' ? null : s.trim());

export function CatalogProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const is_new = id === 'new';

  const { data: product, isLoading, error } = use_catalog_product(is_new ? undefined : id);

  const [vertical, set_vertical] = useState<Vertical>('FOOD');
  const [draft, set_draft] = useState<Draft>(empty_draft());
  const { data: all_categories } = use_product_categories();
  const [form_error, set_form_error] = useState<string | null>(null);

  const create_m = use_create_catalog_product();
  const update_m = use_update_catalog_product();
  const commission_m = use_set_catalog_commission();
  const busy = create_m.isPending || update_m.isPending;

  useEffect(() => {
    if (product) {
      set_draft(from_product(product));
      set_vertical(product.vertical);
    }
  }, [product]);

  useEffect(() => {
    if (is_new) set_draft(empty_draft());
  }, [is_new, vertical]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => set_draft((d) => ({ ...d, [k]: v }));
  const effective_vertical = product?.vertical ?? vertical;

  /*
   * Only categories still in use. A retired one keeps every product already filed under
   * it, but offering it here would let somebody file a new product into a category the
   * shop has stopped using.
   */
  const options = (all_categories ?? []).filter(
    (c) => c.vertical === effective_vertical && (c.is_active || c.key === product?.category),
  );

  // The first category, once the list arrives — a new product cannot be saved without one.
  useEffect(() => {
    if (is_new && !draft.category && options.length > 0) {
      set_draft((d) => ({ ...d, category: options[0].key }));
    }
  }, [is_new, draft.category, options]);

  async function save() {
    set_form_error(null);
    if (!draft.name.trim()) return set_form_error('Give the product a name.');
    const payload = {
      name: draft.name.trim(),
      brand: or_null(draft.brand),
      category: draft.category,
      pack_size: or_null(draft.pack_size),
      description: or_null(draft.description),
      is_prescription_required: draft.is_prescription_required,
    };
    try {
      if (is_new) {
        const created = await create_m.mutateAsync({ vertical, ...payload });
        notify.success('Added to the catalogue', {
          description: 'Now add its photos — vendors linking to it will show them.',
        });
        navigate(`/catalogue/${created.id}`, { replace: true });
      } else {
        await update_m.mutateAsync({ id: id as string, input: payload });
        notify.success('Saved', {
          description: product?.vendor_count
            ? `The change reaches ${product.vendor_count} vendor listing${product.vendor_count === 1 ? '' : 's'}.`
            : 'No vendor lists this yet, so nothing changed for shoppers.',
        });
      }
    } catch (err) {
      set_form_error(err instanceof Error ? err.message : 'Could not save this product.');
    }
  }

  async function set_active(is_active: boolean) {
    if (!product) return;
    try {
      await update_m.mutateAsync({ id: product.id, input: { is_active } });
      notify.success(is_active ? 'Back in the catalogue' : 'Retired from the catalogue', {
        description: is_active
          ? 'Vendors can link to it again.'
          : `It stays on its ${product.vendor_count} existing listing${product.vendor_count === 1 ? '' : 's'}, but no new one can link to it.`,
      });
    } catch (err) {
      notify.error(err, 'Could not change this product.');
    }
  }

  if (error) {
    return (
      <DashboardLayout title="Catalogue product" breadcrumbs={[{ label: 'Product' }]}>
        <ErrorState
          title="Could not load this product"
          description="It may have been removed from the catalogue."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={is_new ? 'New catalogue product' : (product?.name ?? 'Catalogue product')}
      subtitle={
        is_new
          ? 'Every vendor will be able to list this without retyping it'
          : product
            ? reach(product)
            : undefined
      }
      breadcrumbs={[{ label: is_new ? 'New product' : (product?.name ?? 'Product') }]}
      title_badges={
        product ? (
          <>
            <Badge tone={product.is_active ? 'success' : 'neutral'} variant="solid" dot>
              {product.is_active ? 'Live' : 'Retired'}
            </Badge>
            <Badge tone="neutral">{VERTICAL_LABEL[product.vertical]}</Badge>
            {product.is_prescription_required && (
              <Badge tone="destructive">
                <PillBottle className="h-3 w-3" /> Prescription
              </Badge>
            )}
          </>
        ) : undefined
      }
      actions={
        product ? (
          <div className="flex flex-wrap gap-2">
            {product.vendor_count > 0 && (
              <Button asChild variant="outline" size="sm">
                <Link
                  to={`/product-approvals?segment=live&q=${encodeURIComponent(product.name)}`}
                  data-testid="see-listings"
                >
                  <Store className="mr-1 h-4 w-4" /> See the {product.vendor_count} listing
                  {product.vendor_count === 1 ? '' : 's'}
                </Link>
              </Button>
            )}
            {/* Retire rather than delete: listings already linked keep working. */}
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => void set_active(!product.is_active)}
              data-testid="toggle-active"
            >
              <Undo2 className="mr-1 h-4 w-4" />
              {product.is_active ? 'Retire' : 'Bring it back'}
            </Button>
          </div>
        ) : undefined
      }
      stats={is_new ? undefined : <MetricStrip metrics={build_metrics(product, all_categories)} />}
    >
      <div className="grid items-start gap-gutter lg:grid-cols-[1.3fr_1fr]">
        <div className="grid gap-gutter">
          <DetailPanel
            title={is_new ? 'What is it' : 'Details'}
            icon={Info}
            description={
              is_new
                ? 'Vendors will list against exactly these words'
                : 'Vendors inherit every field here — they cannot change any of it'
            }
            test_id="panel-details"
            actions={
              <Button size="sm" onClick={() => void save()} disabled={busy} data-testid="catalog-save">
                <Save className="mr-1 h-4 w-4" />
                {is_new ? 'Add to catalogue' : 'Save changes'}
              </Button>
            }
          >
            {isLoading && !is_new ? (
              <PanelSkeleton rows={5} />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2" data-testid="catalog-form">
                {is_new && (
                  <div className="grid gap-1.5 sm:col-span-2">
                    <Label htmlFor="c-vertical">Which shop</Label>
                    <select
                      id="c-vertical"
                      className="console-select"
                      data-testid="catalog-vertical"
                      value={vertical}
                      onChange={(e) => set_vertical(e.target.value as Vertical)}
                    >
                      <option value="FOOD">{VERTICAL_LABEL.FOOD}</option>
                      <option value="PHARMACY">{VERTICAL_LABEL.PHARMACY}</option>
                    </select>
                    <p className="text-micro text-muted-foreground">
                      Fixed once saved — the two shops do not share a category list.
                    </p>
                  </div>
                )}

                <div className="grid gap-1.5">
                  <Label htmlFor="c-name">Name</Label>
                  <input
                    id="c-name"
                    className="console-field"
                    data-testid="catalog-name"
                    value={draft.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="Pedigree Adult Dry Dog Food"
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="c-brand">Brand</Label>
                  <input
                    id="c-brand"
                    className="console-field"
                    data-testid="catalog-brand"
                    value={draft.brand}
                    onChange={(e) => set('brand', e.target.value)}
                    placeholder="Pedigree"
                  />
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="c-category">Category</Label>
                  <select
                    id="c-category"
                    className="console-select"
                    data-testid="catalog-category"
                    value={draft.category}
                    onChange={(e) => set('category', e.target.value)}
                  >
                    {options.map((c) => (
                      <option key={c.id} value={c.key}>
                        {c.label}
                        {c.is_active ? '' : ' (retired)'}
                      </option>
                    ))}
                  </select>
                  <p className="text-micro text-muted-foreground">
                    Also decides which fee rule applies when this product sets none of its own.
                  </p>
                </div>

                <div className="grid gap-1.5">
                  <Label htmlFor="c-pack">Pack size</Label>
                  <input
                    id="c-pack"
                    className="console-field"
                    data-testid="catalog-pack-size"
                    value={draft.pack_size}
                    onChange={(e) => set('pack_size', e.target.value)}
                    placeholder="3 kg"
                  />
                </div>

                <div className="grid gap-1.5 sm:col-span-2">
                  <Label htmlFor="c-desc">Description</Label>
                  <textarea
                    id="c-desc"
                    className="console-field min-h-20 py-2"
                    data-testid="catalog-description"
                    value={draft.description}
                    onChange={(e) => set('description', e.target.value)}
                  />
                </div>

                {effective_vertical === 'PHARMACY' && (
                  <label className="flex items-center gap-2 text-data sm:col-span-2">
                    <input
                      type="checkbox"
                      className="console-checkbox"
                      checked={draft.is_prescription_required}
                      onChange={(e) => set('is_prescription_required', e.target.checked)}
                      data-testid="catalog-rx"
                    />
                    {/*
                      Set centrally on purpose: it is the one field that exists so a single
                      vendor cannot quietly list an Rx medicine as over the counter.
                    */}
                    Prescription required — no vendor can override this
                  </label>
                )}

                {form_error && (
                  <div className="sm:col-span-2">
                    <ErrorState title="Could not save" description={form_error} />
                  </div>
                )}
              </div>
            )}
          </DetailPanel>

          <DetailPanel
            title="Photos"
            icon={Images}
            description="Every vendor selling this shows them — the first is what a card uses"
            test_id="panel-photos"
          >
            {is_new ? (
              /* Photos need a product id to attach to, so they come after the first save. */
              <p className="text-data text-muted-foreground" data-testid="catalog-gallery-later">
                Save the product first, then add its photos here.
              </p>
            ) : isLoading || !product ? (
              <PanelSkeleton rows={2} />
            ) : (
              <CatalogGalleryEditor product={product} />
            )}
          </DetailPanel>
        </div>

        <div className="grid gap-gutter">
          <DetailPanel
            title="Platform fee"
            icon={Percent}
            description="What the platform charges on this product, whoever sells it"
            test_id="panel-fee"
          >
            {is_new ? (
              <p className="text-data text-muted-foreground">
                Once saved, this product rides its category’s default until you set a fee here.
              </p>
            ) : isLoading || !product ? (
              <PanelSkeleton rows={2} />
            ) : (
              <div className="grid gap-3">
                <FieldList>
                  <Field
                    label="Charges today"
                    value={
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold tabular" data-testid="fee-effective">
                          {fee_amount(product.effective)}
                        </span>
                        <Badge tone={FEE_SOURCE_TONE[product.effective.source]} size="sm">
                          {product.effective.source === 'CATEGORY'
                            ? `${category_label(all_categories, product.vertical, product.category)} default`
                            : FEE_SOURCE_LABEL[product.effective.source]}
                        </Badge>
                      </span>
                    }
                  />
                </FieldList>
                {/*
                  Wider reach than a per-listing override: set here it applies to every vendor
                  selling this product, so a well-known item is priced once instead of once
                  per vendor.
                */}
                <CommissionEditor
                  testid={product.id}
                  value={product.commission}
                  effective={product.effective}
                  inherited_label={`${category_label(all_categories, product.vertical, product.category)} default`}
                  saving={commission_m.isPending}
                  on_save={async (next) => {
                    await commission_m.mutateAsync({ id: product.id, ...next });
                  }}
                />
                <p className="text-micro text-muted-foreground">
                  A vendor’s own listing, or one size of it, can still override this.{' '}
                  <Link to="/platform-fees" className="underline underline-offset-2 hover:text-primary">
                    Category defaults live on Platform fees.
                  </Link>
                </p>
              </div>
            )}
          </DetailPanel>

          {!is_new && (
            <DetailPanel
              title="Who is selling it"
              icon={Users}
              description="The reach of every edit on this page"
              test_id="panel-reach"
            >
              {isLoading || !product ? (
                <PanelSkeleton rows={2} />
              ) : (
                <div className="grid gap-2">
                  <p className="text-data" data-testid="catalog-vendor-count">
                    {reach(product)}
                  </p>
                  {product.vendor_count > 0 && (
                    <Button asChild variant="outline" size="sm" className="w-fit">
                      <Link
                        to={`/product-approvals?segment=live&q=${encodeURIComponent(product.name)}`}
                      >
                        <Store className="mr-1 h-4 w-4" /> Open those listings
                      </Link>
                    </Button>
                  )}
                  <FieldList className="mt-1">
                    <Field label="Catalogue ID" value={product.id} mono />
                    <Field label="Last changed" value={date_time(product.updated_at)} />
                  </FieldList>
                </div>
              )}
            </DetailPanel>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function build_metrics(
  p: CatalogProduct | undefined,
  categories: ProductCategory[] | undefined,
): Metric[] {
  const photos = p ? photo_count(p) : 0;
  return [
    {
      key: 'vendors',
      label: 'Vendors selling it',
      value: p ? String(p.vendor_count) : undefined,
      hint: p && p.vendor_count === 0 ? 'Nobody has picked it up' : 'An edit reaches all of them',
      tone: p && p.vendor_count === 0 ? 'warning' : 'default',
      icon: Users,
      loading: !p,
    },
    {
      key: 'photos',
      label: 'Photos',
      value: p ? String(photos) : undefined,
      hint: photos === 0 ? 'Shoppers see an empty slot' : 'First one leads',
      tone: p && photos === 0 ? 'warning' : 'default',
      icon: Images,
      loading: !p,
    },
    {
      key: 'fee',
      label: 'Charges',
      value: p ? fee_amount(p.effective) : undefined,
      hint: p ? FEE_SOURCE_LABEL[p.effective.source] : undefined,
      tone: p?.effective.source === 'NONE' ? 'destructive' : 'default',
      icon: Percent,
      loading: !p,
    },
    {
      key: 'category',
      label: 'Category',
      value: p ? category_label(categories, p.vertical, p.category) : undefined,
      hint: p ? VERTICAL_LABEL[p.vertical] : undefined,
      icon: Boxes,
      loading: !p,
    },
    {
      key: 'changed',
      label: 'Last changed',
      value: p ? ago(p.updated_at) : undefined,
      icon: Clock,
      loading: !p,
    },
  ];
}
